import { useState, useEffect, useCallback } from "react";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ax/Button";
import {
  adminGetCatalog,
  adminSaveBook,
  adminSetBookStatus,
  adminSaveChapter,
  adminGetChapterMarkdown,
  adminDeleteChapter,
  checkAdminClaim,
} from "@/lib/adminLibraryStore";

export default function AdminLibrary() {
  const { lang } = useI18n();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [books, setBooks] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Editor states
  const [editingBook, setEditingBook] = useState(null); // null, 'new', or book object
  const [selectedBookForChapters, setSelectedBookForChapters] = useState(null);
  const [editingChapter, setEditingChapter] = useState(null); // null, 'new', or { bookId, chapter }
  const [chapterContent, setChapterContent] = useState("");

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    const res = await adminGetCatalog();
    if (res.error === "unauthorized") {
      setIsAdmin(false);
      setLoading(false);
      return;
    }
    if (res.error) {
      setErrorMsg("Gagal memuat katalog admin.");
      setLoading(false);
      return;
    }
    setIsAdmin(true);
    setBooks(res.books || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAdminClaim().then((hasClaim) => {
      if (!hasClaim) {
        // Coba loadCatalog sekali untuk verifikasi via callable error
        loadCatalog();
      } else {
        setIsAdmin(true);
        loadCatalog();
      }
    });
  }, [loadCatalog]);

  const handleToggleStatus = async (book) => {
    const nextStatus = book.status === "published" ? "draft" : "published";
    setErrorMsg("");
    setSuccessMsg("");
    const res = await adminSetBookStatus(book.id, nextStatus);
    if (res.ok) {
      setSuccessMsg(`Status buku "${book.id}" diubah menjadi ${nextStatus}.`);
      await loadCatalog();
    } else {
      setErrorMsg(res.error || "Gagal mengubah status buku.");
    }
  };

  const handleSaveBookSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");
    const formData = new FormData(e.currentTarget);
    const bookData = {
      bookId: formData.get("bookId")?.toString().trim().toLowerCase(),
      title: {
        id: formData.get("title_id")?.toString().trim(),
        en: formData.get("title_en")?.toString().trim(),
      },
      category: {
        id: formData.get("category_id")?.toString().trim(),
        en: formData.get("category_en")?.toString().trim(),
      },
      summary: {
        id: formData.get("summary_id")?.toString().trim(),
        en: formData.get("summary_en")?.toString().trim(),
      },
      minutes: Number(formData.get("minutes")) || 0,
      status: formData.get("status")?.toString() || "draft",
    };

    const res = await adminSaveBook(bookData);
    if (res.ok) {
      setSuccessMsg(`Buku "${bookData.bookId}" berhasil disimpan.`);
      setEditingBook(null);
      await loadCatalog();
    } else {
      setErrorMsg(res.error || "Gagal menyimpan buku.");
    }
  };

  const handleEditChapterClick = async (bookId, chapter) => {
    setErrorMsg("");
    setEditingChapter({ bookId, chapterId: chapter.id, title: chapter.title, minutes: chapter.minutes, tags: chapter.tags });
    // Ambil isi markdown privat via Admin SDK
    const res = await adminGetChapterMarkdown(bookId, chapter.id);
    if (res.ok) {
      setChapterContent(res.content);
    } else {
      setChapterContent("");
      setErrorMsg("Gagal memuat isi markdown bab.");
    }
  };

  const handleSaveChapterSubmit = async (e) => {
    e.preventDefault();
    if (!editingChapter?.bookId) return;
    setErrorMsg("");
    setSuccessMsg("");
    const formData = new FormData(e.currentTarget);
    const rawTags = formData.get("tags")?.toString() || "";
    const tags = rawTags.split(",").map((t) => t.trim()).filter(Boolean);

    const chapterData = {
      bookId: editingChapter.bookId,
      chapterId: formData.get("chapterId")?.toString().trim().toLowerCase(),
      title: {
        id: formData.get("title_id")?.toString().trim(),
        en: formData.get("title_en")?.toString().trim(),
      },
      minutes: Number(formData.get("minutes")) || 5,
      tags,
      content: chapterContent,
    };

    const res = await adminSaveChapter(chapterData);
    if (res.ok) {
      setSuccessMsg(`Bab "${chapterData.chapterId}" berhasil disimpan ke Storage privat.`);
      setEditingChapter(null);
      setChapterContent("");
      await loadCatalog();
    } else {
      setErrorMsg(res.error || "Gagal menyimpan bab.");
    }
  };

  const handleDeleteChapter = async (bookId, chapterId) => {
    if (bookId === "membingkai-pertanyaan") {
      alert("Buku uji membingkai-pertanyaan tidak boleh diubah susunan babnya.");
      return;
    }
    if (!window.confirm(`Hapus bab ${chapterId}?`)) return;
    const res = await adminDeleteChapter(bookId, chapterId);
    if (res.ok) {
      setSuccessMsg(`Bab ${chapterId} dihapus.`);
      await loadCatalog();
    } else {
      setErrorMsg(res.error || "Gagal menghapus bab.");
    }
  };

  if (loading) {
    return (
      <div data-testid="admin-loading" className="py-12 text-center">
        <p className="text-base text-ax-text">Memuat CMS Library...</p>
      </div>
    );
  }

  // Jika bukan admin: Halaman ini tidak tersedia
  if (!isAdmin) {
    return (
      <div data-testid="admin-unauthorized" className="py-12 text-center">
        <p className="text-base text-ax-text">Halaman ini tidak tersedia.</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-library-page" className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="type-h1 m-0 text-xl font-semibold">CMS Library</h1>
            <span className="rounded-[4px] bg-ax-primary/10 px-2 py-0.5 text-xs font-mono font-medium text-ax-primary">
              ADMIN
            </span>
          </div>
          <p className="caption m-0 mt-1 text-sm text-ax-muted">
            Kelola katalog buku, status publikasi, dan naskah bab Storage privat.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="admin-new-book-button"
            onClick={() => {
              setEditingBook(editingBook ? null : "new");
              setEditingChapter(null);
            }}
          >
            {editingBook === "new" ? "Batal" : "+ Buku Baru"}
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {errorMsg && (
        <div role="alert" className="rounded-[8px] border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-400">
          {errorMsg}
        </div>
      )}
      {successMsg && (
        <div role="status" className="rounded-[8px] border border-emerald-500/20 bg-emerald-500/10 p-3 text-sm text-emerald-400">
          {successMsg}
        </div>
      )}

      {/* Form: Tambah / Edit Buku */}
      {editingBook && (
        <div data-testid="admin-book-form-card" className="rounded-[12px] border border-ax-border bg-white/5 p-5">
          <h2 className="type-h2 m-0 mb-4 text-lg font-medium">
            {editingBook === "new" ? "Tambah Buku Baru" : `Edit Buku: ${editingBook.id}`}
          </h2>
          <form onSubmit={handleSaveBookSubmit} className="flex flex-col gap-4">
            <div>
              <label htmlFor="bookId" className="block text-xs font-medium text-ax-muted">ID / Slug (huruf kecil & strip)</label>
              <input
                id="bookId"
                name="bookId"
                required
                pattern="^[a-z0-9-]+$"
                defaultValue={editingBook !== "new" ? editingBook.id : ""}
                readOnly={editingBook !== "new"}
                className="mt-1 w-full rounded-[6px] border border-ax-border bg-ax-bg px-3 py-2 text-sm text-ax-text outline-none focus:border-ax-primary"
                placeholder="misal: navigasi-strategis"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="title_id" className="block text-xs font-medium text-ax-muted">Judul (Indonesia)</label>
                <input
                  id="title_id"
                  name="title_id"
                  required
                  defaultValue={editingBook !== "new" ? editingBook.title?.id : ""}
                  className="mt-1 w-full rounded-[6px] border border-ax-border bg-ax-bg px-3 py-2 text-sm text-ax-text outline-none focus:border-ax-primary"
                />
              </div>
              <div>
                <label htmlFor="title_en" className="block text-xs font-medium text-ax-muted">Judul (English)</label>
                <input
                  id="title_en"
                  name="title_en"
                  required
                  defaultValue={editingBook !== "new" ? editingBook.title?.en : ""}
                  className="mt-1 w-full rounded-[6px] border border-ax-border bg-ax-bg px-3 py-2 text-sm text-ax-text outline-none focus:border-ax-primary"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="category_id" className="block text-xs font-medium text-ax-muted">Kategori (Indonesia)</label>
                <input
                  id="category_id"
                  name="category_id"
                  required
                  defaultValue={editingBook !== "new" ? editingBook.category?.id : "Kerangka"}
                  className="mt-1 w-full rounded-[6px] border border-ax-border bg-ax-bg px-3 py-2 text-sm text-ax-text outline-none focus:border-ax-primary"
                />
              </div>
              <div>
                <label htmlFor="category_en" className="block text-xs font-medium text-ax-muted">Kategori (English)</label>
                <input
                  id="category_en"
                  name="category_en"
                  required
                  defaultValue={editingBook !== "new" ? editingBook.category?.en : "Framing"}
                  className="mt-1 w-full rounded-[6px] border border-ax-border bg-ax-bg px-3 py-2 text-sm text-ax-text outline-none focus:border-ax-primary"
                />
              </div>
            </div>

            <div>
              <label htmlFor="summary_id" className="block text-xs font-medium text-ax-muted">Ringkasan (Indonesia)</label>
              <textarea
                id="summary_id"
                name="summary_id"
                required
                rows={2}
                defaultValue={editingBook !== "new" ? editingBook.summary?.id : ""}
                className="mt-1 w-full rounded-[6px] border border-ax-border bg-ax-bg px-3 py-2 text-sm text-ax-text outline-none focus:border-ax-primary"
              />
            </div>
            <div>
              <label htmlFor="summary_en" className="block text-xs font-medium text-ax-muted">Ringkasan (English)</label>
              <textarea
                id="summary_en"
                name="summary_en"
                required
                rows={2}
                defaultValue={editingBook !== "new" ? editingBook.summary?.en : ""}
                className="mt-1 w-full rounded-[6px] border border-ax-border bg-ax-bg px-3 py-2 text-sm text-ax-text outline-none focus:border-ax-primary"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="minutes" className="block text-xs font-medium text-ax-muted">Estimasi Menit</label>
                <input
                  id="minutes"
                  name="minutes"
                  type="number"
                  defaultValue={editingBook !== "new" ? editingBook.minutes : 15}
                  className="mt-1 w-full rounded-[6px] border border-ax-border bg-ax-bg px-3 py-2 text-sm text-ax-text outline-none focus:border-ax-primary"
                />
              </div>
              <div>
                <label htmlFor="status" className="block text-xs font-medium text-ax-muted">Status Awal</label>
                <select
                  id="status"
                  name="status"
                  defaultValue={editingBook !== "new" ? editingBook.status : "draft"}
                  className="mt-1 w-full rounded-[6px] border border-ax-border bg-ax-bg px-3 py-2 text-sm text-ax-text outline-none focus:border-ax-primary"
                >
                  <option value="draft">Draft (Tertutup)</option>
                  <option value="published">Published (Terbit)</option>
                </select>
              </div>
            </div>

            <div className="mt-2 flex gap-2">
              <Button type="submit" data-testid="admin-save-book-button">Simpan Buku</Button>
              <Button variant="secondary" onClick={() => setEditingBook(null)}>Batal</Button>
            </div>
          </form>
        </div>
      )}

      {/* Form: Tambah / Edit Bab */}
      {editingChapter && (
        <div data-testid="admin-chapter-form-card" className="rounded-[12px] border border-ax-primary/40 bg-white/5 p-5">
          <h2 className="type-h2 m-0 mb-4 text-lg font-medium">
            {editingChapter.chapterId ? `Edit Bab: ${editingChapter.chapterId}` : `Tambah Bab Baru: Buku ${editingChapter.bookId}`}
          </h2>
          <form onSubmit={handleSaveChapterSubmit} className="flex flex-col gap-4">
            <div>
              <label htmlFor="chapterId" className="block text-xs font-medium text-ax-muted">ID Bab / Slug (misal: b1, b2)</label>
              <input
                id="chapterId"
                name="chapterId"
                required
                pattern="^[a-z0-9-]+$"
                defaultValue={editingChapter.chapterId || ""}
                readOnly={!!editingChapter.chapterId}
                className="mt-1 w-full rounded-[6px] border border-ax-border bg-ax-bg px-3 py-2 text-sm text-ax-text outline-none focus:border-ax-primary"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="chapter_title_id" className="block text-xs font-medium text-ax-muted">Judul Bab (Indonesia)</label>
                <input
                  id="chapter_title_id"
                  name="title_id"
                  required
                  defaultValue={editingChapter.title?.id || ""}
                  className="mt-1 w-full rounded-[6px] border border-ax-border bg-ax-bg px-3 py-2 text-sm text-ax-text outline-none focus:border-ax-primary"
                />
              </div>
              <div>
                <label htmlFor="chapter_title_en" className="block text-xs font-medium text-ax-muted">Judul Bab (English)</label>
                <input
                  id="chapter_title_en"
                  name="title_en"
                  required
                  defaultValue={editingChapter.title?.en || ""}
                  className="mt-1 w-full rounded-[6px] border border-ax-border bg-ax-bg px-3 py-2 text-sm text-ax-text outline-none focus:border-ax-primary"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="chapter_minutes" className="block text-xs font-medium text-ax-muted">Estimasi Menit</label>
                <input
                  id="chapter_minutes"
                  name="minutes"
                  type="number"
                  defaultValue={editingChapter.minutes || 5}
                  className="mt-1 w-full rounded-[6px] border border-ax-border bg-ax-bg px-3 py-2 text-sm text-ax-text outline-none focus:border-ax-primary"
                />
              </div>
              <div>
                <label htmlFor="chapter_tags" className="block text-xs font-medium text-ax-muted">Tags (pisahkan koma)</label>
                <input
                  id="chapter_tags"
                  name="tags"
                  defaultValue={Array.isArray(editingChapter.tags) ? editingChapter.tags.join(", ") : ""}
                  className="mt-1 w-full rounded-[6px] border border-ax-border bg-ax-bg px-3 py-2 text-sm text-ax-text outline-none focus:border-ax-primary"
                  placeholder="kerangka, batas"
                />
              </div>
            </div>

            <div>
              <label htmlFor="chapter_content" className="block text-xs font-medium text-ax-muted">Naskah Markdown Bab</label>
              <textarea
                id="chapter_content"
                name="content"
                required
                rows={10}
                value={chapterContent}
                onChange={(e) => setChapterContent(e.target.value)}
                className="mt-1 w-full font-mono rounded-[6px] border border-ax-border bg-ax-bg px-3 py-2 text-sm text-ax-text outline-none focus:border-ax-primary"
                placeholder="# Judul Bab&#10;&#10;Isi teks naskah Markdown..."
              />
            </div>

            <div className="mt-2 flex gap-2">
              <Button type="submit" data-testid="admin-save-chapter-button">Simpan Naskah Bab</Button>
              <Button variant="secondary" onClick={() => setEditingChapter(null)}>Batal</Button>
            </div>
          </form>
        </div>
      )}

      {/* Book List Table / Cards */}
      <div className="flex flex-col gap-4">
        {books.map((b) => {
          const isPublished = b.status === "published";
          const titleText = b.title?.[lang] || b.title?.id || b.id;
          const categoryText = b.category?.[lang] || b.category?.id || "";

          return (
            <div
              key={b.id}
              data-testid={`admin-book-card-${b.id}`}
              className="flex flex-col gap-4 rounded-[12px] border border-ax-border bg-white/[0.02] p-5"
            >
              <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="type-h3 m-0 text-base font-semibold text-ax-text">{titleText}</h3>
                    <span
                      data-testid={`book-status-${b.id}`}
                      className={`rounded-[4px] px-2 py-0.5 text-xs font-mono font-medium ${
                        isPublished
                          ? "border border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
                          : "border border-amber-500/30 bg-amber-500/10 text-amber-400"
                      }`}
                    >
                      {isPublished ? "PUBLISHED" : "DRAFT"}
                    </span>
                  </div>
                  <p className="caption m-0 mt-1 text-xs text-ax-muted">
                    ID: <code className="font-mono text-ax-text-2">{b.id}</code> · {categoryText} · {b.minutes} mnt · {b.chapters?.length || 0} bab
                  </p>
                  <p className="m-0 mt-2 text-sm leading-5 text-ax-text-2">
                    {b.summary?.[lang] || b.summary?.id}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="secondary"
                    data-testid={`toggle-status-${b.id}`}
                    onClick={() => handleToggleStatus(b)}
                  >
                    {isPublished ? "Turunkan (Draft)" : "Publikasikan"}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setEditingBook(b);
                      setEditingChapter(null);
                    }}
                  >
                    Edit Metadata
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setSelectedBookForChapters(selectedBookForChapters === b.id ? null : b.id);
                    }}
                  >
                    {selectedBookForChapters === b.id ? "Tutup Bab" : `Lihat Bab (${b.chapters?.length || 0})`}
                  </Button>
                </div>
              </div>

              {/* Sub-list Chapters if expanded */}
              {selectedBookForChapters === b.id && (
                <div className="mt-2 rounded-[8px] border border-ax-border bg-black/20 p-4">
                  <div className="flex items-center justify-between pb-3">
                    <h4 className="type-h4 m-0 text-sm font-medium text-ax-muted">
                      Daftar Bab: {b.id}
                    </h4>
                    <Button
                      size="sm"
                      data-testid={`admin-add-chapter-${b.id}`}
                      onClick={() => {
                        setEditingChapter({ bookId: b.id });
                        setChapterContent("");
                      }}
                    >
                      + Tambah Bab
                    </Button>
                  </div>

                  {(!b.chapters || b.chapters.length === 0) ? (
                    <p className="m-0 text-xs text-ax-muted">Belum ada bab.</p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {b.chapters.map((ch) => (
                        <div
                          key={ch.id}
                          className="flex items-center justify-between rounded-[6px] border border-ax-border/50 bg-white/[0.01] px-3 py-2 text-sm"
                        >
                          <div>
                            <span className="font-mono text-xs text-ax-primary">{ch.id}</span>:{" "}
                            <span className="font-medium">{ch.title?.[lang] || ch.title?.id}</span>{" "}
                            <span className="text-xs text-ax-muted">({ch.minutes} mnt)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              data-testid={`edit-chapter-${b.id}-${ch.id}`}
                              onClick={() => handleEditChapterClick(b.id, ch)}
                            >
                              Edit Naskah
                            </Button>
                            {b.id !== "membingkai-pertanyaan" && (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeleteChapter(b.id, ch.id)}
                              >
                                Hapus
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
